/**
 * The cabin the pilot is sitting in — floor, walls, pillars, roof, seat and console.
 *
 * ## Why this exists
 *
 * A pilot on the live build said the cockpit "looks like shit", and the daylight screenshots agreed
 * for a reason nobody had written down: **there was no cockpit.** `cockpit.ts` built an instrument
 * panel, a padded coaming, two short posts and a yoke, and stopped. Everything else — the
 * windscreen frame, the roof, the side walls, the floor, the seat, the door pillars — did not
 * exist, so the panel hung in the middle of an otherwise unobstructed view with terrain visible
 * above it, below it, and to either side of the two posts, which themselves ended in mid-air.
 *
 * That reads as a dashboard floating in space rather than as an aeroplane, and no amount of
 * material work on the panel would have changed it. The enclosure is the thing.
 *
 * ## What an enclosure is actually for
 *
 * Not decoration. Three jobs, and they are the reason the shape below is what it is:
 *
 *   - **It frames the view.** A cockpit tells you where you are looking by what it hides. The
 *     window apertures here are cut by *placing solid geometry around them*, so the opening is
 *     the shape a real one is: bounded below by the sill, above by the roof rail, forward by the
 *     windscreen pillar.
 *   - **It gives the eye a scale reference.** Terrain streamed from orbit has no size until
 *     something a known size is in front of it. A seat back and a door frame do that.
 *   - **It occludes.** Whatever is behind the pilot does not need drawing, and the bulkhead says
 *     so to the depth buffer.
 *
 * ## Draw calls
 *
 * Every static piece is a transformed box, and boxes sharing a material are **merged into one
 * geometry** before they reach the scene. The whole cabin is therefore four meshes — interior,
 * trim, metal, upholstery — rather than the forty-odd it is built from, which matters because
 * CLAUDE.md allows 350 draw calls for the entire frame and terrain at low altitude already asks
 * for nearly two hundred.
 *
 * `updateFixed` / `updateFrame`: neither. Built once; nothing here moves.
 */

import { BoxGeometry, Group, Mesh, type BufferGeometry, type Material } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/**
 * The cabin's dimensions, in the cockpit structure's own frame.
 *
 * The origin is `STRUCTURE_BELOW_EYE_M` below the pilot's eye and directly under it, so every `y`
 * here is measured against the seated eye height and every `z` against the eye's fore-aft
 * position — negative forward. Those two relationships are the ones that decide whether the
 * aeroplane can be flown from this seat, which is why the numbers live together in one struct
 * rather than being scattered through the builder as literals. See `STRUCTURE_BELOW_EYE_M` in
 * `cockpit.ts` for what happened the last time a cockpit's geometry and its eye point were only
 * related by hand.
 */
export interface CabinShape {
  /** Half the cabin's internal width at the pilot's shoulder [m]. */
  halfWidthM: number;
  /** Roof height [m]. Positive is above the structure origin. */
  roofM: number;
  /** Window sill height [m]. */
  sillM: number;
  /** Floor height [m]. */
  floorM: number;
  /** Where the windscreen's base sits [m], forward of the eye. */
  windscreenBaseZ: number;
  /** Where the windscreen meets the roof [m]. */
  windscreenTopZ: number;
  /** Aft end of the cabin [m], behind the eye. */
  aftZ: number;
  /** Aft edge of the side window — the door's rear pillar [m]. */
  windowAftZ: number;
  /** Forward edge of the side window [m]. Forward of this the sill rises to the glareshield. */
  windowFwdZ: number;
  /**
   * How high the sidewall reaches forward of the side window [m] — the beltline.
   *
   * Every light aeroplane's window line sweeps *up* toward the nose to meet the glareshield, and
   * leaving it flat opens a wedge of daylight between the outboard end of the instrument panel
   * and the base of the windscreen pillar. That wedge is visible in the first screenshot of this
   * cabin: a triangle of terrain where the aeroplane's skin should be. It costs one box a side,
   * and it blocks nothing forward — the wall is a vertical plane facing inboard, and the
   * windscreen aperture is between the pillars, well inboard of it.
   */
  beltlineM: number;
  /**
   * `cabin` is a light aeroplane: square windows, a door frame each side, and a centre post
   * splitting the windscreen. `canopy` is a single-piece bubble: one bow frame forward, rails
   * along the sills, and no centre post — which is the whole reason a fighter pilot can see.
   */
  style: 'cabin' | 'canopy';
}

/**
 * The three cabins, by control type and panel.
 *
 * Derived from the profile rather than from an aircraft id, for the same reason the rest of the
 * cockpit is: non-negotiable #8 says the aeroplane is data and this file is not allowed to know
 * which one it is building for.
 */
export function cabinShapeFor(profile: {
  control: 'yoke' | 'stick';
  gaugeCount: number;
}): CabinShape {
  // A yoke means a cabin: side-by-side seating, doors, a wide flat panel. A stick means a
  // single-seat bubble, which is narrower, taller over the head, and open at the sides.
  if (profile.control === 'yoke') {
    return {
      halfWidthM: 0.52,
      roofM: 0.46,
      sillM: -0.02,
      floorM: -0.62,
      windscreenBaseZ: -0.78,
      windscreenTopZ: -0.28,
      aftZ: 0.66,
      windowAftZ: 0.34,
      windowFwdZ: -0.12,
      beltlineM: 0.12,
      style: 'cabin',
    };
  }
  /*
   * The fighter's bubble sits higher and further forward than the aerobat's sliding canopy: the
   * whole point of an F-16's canopy is that the bow frame is ahead of the pilot's shoulders and
   * there is nothing above the eye line at all. `gaugeCount` separates them because the jet
   * carries six standby instruments and the aerobat carries the full set — the same fact
   * `cockpitProfileFor` already used, read here rather than re-derived.
   */
  const isFighter = profile.gaugeCount <= 6;
  return {
    halfWidthM: isFighter ? 0.42 : 0.38,
    roofM: isFighter ? 0.54 : 0.44,
    sillM: isFighter ? -0.06 : -0.04,
    floorM: -0.66,
    windscreenBaseZ: isFighter ? -0.82 : -0.76,
    windscreenTopZ: isFighter ? -0.7 : -0.62,
    aftZ: 0.6,
    windowAftZ: isFighter ? 0.42 : 0.3,
    windowFwdZ: isFighter ? -0.3 : -0.2,
    // A fighter's sill is the top of the side console and it stays low — that is what the bubble
    // is for. The aerobat's rises like a light aeroplane's, because it is one.
    beltlineM: isFighter ? 0.02 : 0.1,
    style: 'canopy',
  };
}

/** The four surfaces a cabin is made of. */
export interface ShellMaterials {
  /** Sidewalls, headliner, bulkhead — the soft-trimmed majority. */
  interior: Material;
  /** Pillars, rails, sills, console — the dark structural trim. */
  trim: Material;
  /** Frame members and the seat rails, where bare metal shows. */
  metal: Material;
  /** Seat cushions and backrest. */
  seat: Material;
}

export interface CockpitShell {
  group: Group;
  /** Everything built, so the cockpit can dispose it. */
  geometries: BufferGeometry[];
}

/**
 * How finely a face is subdivided for the sake of baked occlusion.
 *
 * Ambient occlusion lives in the vertices (see `cockpitAmbient.ts`), and it varies *linearly*
 * across a triangle — so an undivided sidewall is four corners and a bilinear ramp between them,
 * which cannot express "dark where it meets the floor, bright beside the window". Every 18 cm of
 * face gets another division, capped so a large flat panel does not turn into a mesh.
 *
 * Derived from the box's own size rather than passed in at each of the twenty-odd call sites: the
 * thing that decides how much subdivision a panel needs is how big it is, and a parameter would be
 * one more number per call to get wrong.
 */
function aoSegments(sizeM: number): number {
  return Math.min(5, Math.max(1, Math.round(sizeM / 0.18)));
}

/** A box, rotated then moved into place. Returned in the cabin's frame, ready to merge. */
function slab(
  width: number,
  height: number,
  depth: number,
  x: number,
  y: number,
  z: number,
  rotateXRad = 0,
): BufferGeometry {
  const geometry = new BoxGeometry(
    width,
    height,
    depth,
    aoSegments(width),
    aoSegments(height),
    aoSegments(depth),
  );
  if (rotateXRad !== 0) geometry.rotateX(rotateXRad);
  geometry.translate(x, y, z);
  return geometry;
}

/**
 * A box spanning two points in the y–z plane — a pillar, in other words.
 *
 * Pillars are the one part of a cabin whose *endpoints* are meaningful and whose length and angle
 * are consequences: a windscreen post runs from a known corner of the glareshield to a known
 * corner of the roof, and computing the rotation by hand from those four numbers is how a frame
 * ends up not quite touching what it is supposed to join.
 */
function strut(
  width: number,
  thickness: number,
  x: number,
  fromY: number,
  fromZ: number,
  toY: number,
  toZ: number,
): BufferGeometry {
  const dy = toY - fromY;
  const dz = toZ - fromZ;
  const length = Math.hypot(dy, dz);
  // A box's length runs along y before rotation; atan2 gives the angle that lays it on the span.
  return slab(
    width,
    length,
    thickness,
    x,
    (fromY + toY) / 2,
    (fromZ + toZ) / 2,
    Math.atan2(dz, dy),
  );
}

/**
 * Build the cabin.
 *
 * Everything is collected per material and merged, so the caller gets four meshes and one array
 * of geometries to dispose.
 */
export function buildCockpitShell(shape: CabinShape, materials: ShellMaterials): CockpitShell {
  const group = new Group();
  group.name = 'cockpit-shell';

  const interior: BufferGeometry[] = [];
  const trim: BufferGeometry[] = [];
  const metal: BufferGeometry[] = [];
  const seat: BufferGeometry[] = [];

  const halfWidth = shape.halfWidthM;
  const width = halfWidth * 2;
  const cabinDepth = shape.aftZ - shape.windscreenTopZ;
  const cabinMidZ = (shape.aftZ + shape.windscreenTopZ) / 2;
  const wallThickness = 0.03;
  /*
   * How far the window frame sits outboard of the tub it is bolted to.
   *
   * Zero for a cabin — a door frame is flush with the fuselage side. Positive for a canopy,
   * because the glass bulges past the shoulder line, which is why a bubble canopy looks like a
   * bubble and why its pilot can see almost straight down the side of the aeroplane. Built flush,
   * as the first version was, the bow lands about 30° off the nose — right in the middle of the
   * sector a pilot uses — and `cockpitSightline.test.ts` measured the two canopy aircraft with
   * *worse* horizon visibility than the cabin: 77 % and 83 % against 92 %, which is backwards.
   */
  const glassOutboardM = shape.style === 'canopy' ? 0.07 : 0;

  // --- floor and footwell ------------------------------------------------------------------
  // The floor runs forward under the panel, because a pilot's feet are on the rudder pedals well
  // ahead of the seat and the gap between floor and panel is otherwise a window onto the terrain.
  const floorDepth = shape.aftZ - shape.windscreenBaseZ;
  interior.push(
    slab(width, 0.03, floorDepth, 0, shape.floorM, (shape.aftZ + shape.windscreenBaseZ) / 2),
  );
  // A kick panel closing the front of the footwell, under the instrument panel.
  interior.push(
    slab(width, 0.3, wallThickness, 0, shape.floorM + 0.15, shape.windscreenBaseZ + 0.015),
  );

  // --- side walls, from floor to sill --------------------------------------------------------
  for (const side of [-1, 1]) {
    interior.push(
      slab(
        wallThickness,
        shape.sillM - shape.floorM,
        floorDepth,
        side * halfWidth,
        (shape.sillM + shape.floorM) / 2,
        (shape.aftZ + shape.windscreenBaseZ) / 2,
      ),
    );
    // The sill itself — a capping rail, in trim, because on every aeroplane it is the edge the
    // pilot's arm rests on and it is always a different material from the wall below it.
    trim.push(
      slab(
        wallThickness + 0.02,
        0.04,
        shape.aftZ - shape.windowFwdZ,
        side * halfWidth,
        shape.sillM,
        (shape.aftZ + shape.windowFwdZ) / 2,
      ),
    );
  }

  // --- the beltline, forward of the side window ------------------------------------------------
  for (const side of [-1, 1]) {
    interior.push(
      slab(
        wallThickness,
        shape.beltlineM - shape.sillM,
        shape.windowFwdZ - shape.windscreenBaseZ,
        side * halfWidth,
        (shape.beltlineM + shape.sillM) / 2,
        (shape.windowFwdZ + shape.windscreenBaseZ) / 2,
      ),
    );
  }

  // --- roof ----------------------------------------------------------------------------------
  /*
   * A cabin has one and a canopy does not, and that is the difference between the two aeroplanes
   * rather than a saving.
   *
   * The first version gave every cockpit an opaque roof, which on the two stick aircraft is a lid
   * over a bubble — the one thing a bubble canopy exists to not have. It also cost them the very
   * visibility a canopy is for: the sightline test measured the horizon 15–40° off the nose at
   * **77 % open on the aerobat and 83 % on the fighter against 92 % on the trainer**, which is
   * backwards, and it was the roof's supporting pillars sweeping aft into the side view that did
   * it. Above a canopy there is sky.
   */
  if (shape.style === 'cabin') {
    interior.push(slab(width, 0.03, cabinDepth, 0, shape.roofM, cabinMidZ));
    // The header above the windscreen, which is what the eye reads as the top of the aperture.
    trim.push(slab(width, 0.06, 0.05, 0, shape.roofM - 0.03, shape.windscreenTopZ + 0.02));
  } else {
    // The bow: a hoop over the top of the windscreen, and nothing behind it.
    trim.push(
      slab(width + glassOutboardM * 2, 0.05, 0.05, 0, shape.roofM - 0.04, shape.windscreenTopZ + 0.02),
    );
  }

  // --- windscreen pillars --------------------------------------------------------------------
  /*
   * The A-pillar, and its width is a judgement rather than a measurement.
   *
   * It is the one piece of structure that sits in the part of the view a pilot uses, so a
   * millimetre too fat here costs visibility on final approach. Set at the inside edge of the
   * cabin so it reads as thickness rather than as a bar across the window.
   */
  const pillarWidth = shape.style === 'canopy' ? 0.034 : 0.042;
  /*
   * A canopy's frame sits *outboard* of the tub it is bolted to, and that is not a detail.
   *
   * The tub is shoulder-width; the glass bulges past it, which is why a bubble canopy looks like
   * a bubble and why the pilot can see almost straight down the side of the aeroplane. Built flush
   * with the tub — as the first version was — the bow lands about 30° off the nose, right in the
   * middle of the sector a pilot uses, and the sightline test measured the two canopy aircraft as
   * having *worse* horizon visibility than the cabin: 77 % and 83 % against 92 %. Pushing the
   * frame out to where the glass actually is moves the obstruction to the edge of the sector,
   * where a real one is.
   */
  for (const side of [-1, 1]) {
    metal.push(
      strut(
        pillarWidth,
        0.05,
        side * (halfWidth + glassOutboardM - (shape.style === 'cabin' ? pillarWidth * 0.4 : 0)),
        shape.sillM + 0.02,
        shape.windscreenBaseZ + 0.03,
        shape.roofM - 0.02,
        shape.windscreenTopZ,
      ),
    );
  }

  if (shape.style === 'cabin') {
    /*
     * The centre post. A high-wing light aeroplane has a two-piece windscreen with a post down
     * the middle, and leaving it out is one of those absences nobody can name but everybody
     * notices — it is directly ahead, so it is the piece that says "windscreen" rather than
     * "open air".
     */
    metal.push(
      strut(
        0.022,
        0.03,
        0,
        shape.sillM + 0.16,
        shape.windscreenBaseZ + 0.04,
        shape.roofM - 0.02,
        shape.windscreenTopZ + 0.01,
      ),
    );
    // Door pillars, aft of the side windows.
    for (const side of [-1, 1]) {
      trim.push(
        slab(
          wallThickness + 0.02,
          shape.roofM - shape.sillM,
          0.06,
          side * halfWidth,
          (shape.roofM + shape.sillM) / 2,
          shape.windowAftZ,
        ),
      );
    }
  } else {
    /*
     * A canopy has no posts along its sides — that is the entire point of one — so what bounds
     * the aperture is the *rail* the transparency sits in, running from the bow to the aft frame
     * at sill height. It reads as a canopy because it is continuous where a cabin's is broken.
     */
    for (const side of [-1, 1]) {
      metal.push(
        slab(
          0.035,
          0.035,
          shape.windowAftZ - shape.windscreenBaseZ,
          side * (halfWidth + glassOutboardM),
          shape.sillM + 0.03,
          (shape.windowAftZ + shape.windscreenBaseZ) / 2,
        ),
      );
    }
    // The aft canopy frame, arching over behind the pilot's head.
    metal.push(
      slab(width + glassOutboardM * 2, 0.05, 0.05, 0, shape.roofM - 0.02, shape.windowAftZ),
    );
  }

  // --- rear bulkhead and the wall behind the window -------------------------------------------
  interior.push(
    slab(width, shape.roofM - shape.floorM, wallThickness, 0, (shape.roofM + shape.floorM) / 2, shape.aftZ),
  );
  for (const side of [-1, 1]) {
    interior.push(
      slab(
        wallThickness,
        shape.roofM - shape.sillM,
        shape.aftZ - shape.windowAftZ,
        side * halfWidth,
        (shape.roofM + shape.sillM) / 2,
        (shape.aftZ + shape.windowAftZ) / 2,
      ),
    );
  }

  // --- the seat -------------------------------------------------------------------------------
  /*
   * Placed by the eye rather than by a seat dimension, which is the honest direction: the pilot's
   * eye is at the origin plus `STRUCTURE_BELOW_EYE_M`, so the cushion is a seated torso below it
   * and the backrest a shoulder-width behind. Only its edges are ever in frame — the rest is
   * behind the camera — but those edges are most of what makes the view read as *from* somewhere.
   */
  const seatY = shape.floorM + 0.24;
  seat.push(slab(0.46, 0.08, 0.44, 0, seatY, 0.2));
  seat.push(slab(0.46, 0.56, 0.09, 0, seatY + 0.3, 0.42));
  metal.push(slab(0.5, 0.03, 0.5, 0, seatY - 0.06, 0.2));

  // --- console --------------------------------------------------------------------------------
  if (shape.style === 'cabin') {
    // A centre pedestal between the seats: trim flaps, fuel selector, the flap lever's home.
    trim.push(slab(0.2, 0.26, 0.5, 0, shape.floorM + 0.13, -0.16));
  } else {
    // Side consoles: throttle to the left, everything else to the right. A single-seat cockpit
    // puts its switches on the walls because there is no middle.
    for (const side of [-1, 1]) {
      trim.push(
        slab(
          0.16,
          0.12,
          0.5,
          side * (halfWidth - 0.09),
          shape.sillM - 0.07,
          -0.1,
        ),
      );
    }
  }

  // --- merge -----------------------------------------------------------------------------------
  const geometries: BufferGeometry[] = [];
  for (const [parts, material, name] of [
    [interior, materials.interior, 'cabin-interior'],
    [trim, materials.trim, 'cabin-trim'],
    [metal, materials.metal, 'cabin-metal'],
    [seat, materials.seat, 'cabin-seat'],
  ] as const) {
    if (parts.length === 0) continue;
    const merged = mergeGeometries(parts, false);
    // Every source box is disposed either way: `mergeGeometries` copies, and a null return means
    // the parts were mismatched, which would otherwise leak them.
    for (const part of parts) part.dispose();
    if (merged === null) continue;
    merged.computeBoundingSphere();
    geometries.push(merged);
    const mesh = new Mesh(merged, material);
    mesh.name = name;
    // The cabin neither casts into the world's cascades nor receives from them: it is interior
    // geometry lit by the ambient and the flood, and a sun shadow through a windscreen we do not
    // model would put a hard edge across the panel.
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    group.add(mesh);
  }

  return { group, geometries };
}
